import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PersonaPadlockError } from '@technologyfromsage/persona-padlock';
import { of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  first,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import * as URI from 'urijs';

import {
  AnonymousUser,
  AuthService,
  AuthorizationFailedException,
  PersonaUser,
  UserScopesService,
  createPersonaUserFromPkceLogin,
} from '®shared/auth';
import { AppConfig } from '®shared/config';
import { ErrorReportingService } from '®shared/reporting/error-reporting.service';
import { UserService } from '®shared/user';

import { PersonaPadlockService } from '../auth/persona-padlock.service';

import {
  currentAnonymousUser,
  currentPersonaUser,
} from './current-user-and-permissions';
import {
  CurrentUserActionTypes,
  LogInDuringRouteNavigationAction,
  StoreAccessTokenAction,
  StoreCurrentUserAction,
} from './current-user.actions';

@Injectable()
export class CurrentUserEffects {
  constructor(
    private actions$: Actions,
    private appConfig: AppConfig,
    private authService: AuthService,
    private personaPadlockService: PersonaPadlockService,
    private userService: UserService,
    private userScopesService: UserScopesService,
    private errorReportingService: ErrorReportingService,
    private router: Router,
  ) {}

  establishCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentUserActionTypes.REQUEST_CURRENT_USER),
      first(),
      switchMap(() => this.authService.authorizeByUrl()),
      switchMap(oauthUser => {
        if (oauthUser.isAnonymous) {
          return of(
            currentAnonymousUser(this.appConfig, oauthUser as AnonymousUser),
          );
        }
        const personaUser = oauthUser as PersonaUser;
        return this.userService
          .loginWithTarl(personaUser.guid, personaUser.activePid)
          .pipe(
            catchError(() => of(null)),
            map(tarlUser =>
              currentPersonaUser(this.appConfig, personaUser, tarlUser),
            ),
          );
      }),
      map(currentUser => new StoreCurrentUserAction({ currentUser })),
    ),
  );

  completeRedirectLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentUserActionTypes.COMPLETE_REDIRECT_LOGIN),
      first(),
      switchMap(() =>
        this.personaPadlockService.completeRedirectAuthentication(),
      ),
      map(response => {
        const { appState, loginData } = response;

        const personaUser = createPersonaUserFromPkceLogin(loginData);

        return { personaUser, appState };
      }),
      map(({ personaUser, appState }) => {
        const targetUri = new URI(appState.target);
        const isTalisLogin = targetUri.hasQuery('talis_login');

        const requiredScopes =
          this.userScopesService.requiredScopesToAuthorize(isTalisLogin);
        const hasRequiredScopes =
          this.userScopesService.hasSomeScopeOrHigherPrivileges(
            personaUser,
            ...requiredScopes,
          );

        this.authService.updateUser(personaUser);

        if (!hasRequiredScopes) {
          throw new AuthorizationFailedException(
            'You do not have the required permissions to access this page.',
          );
        }

        return { personaUser, targetUri };
      }),
      switchMap(({ personaUser, targetUri }) =>
        this.userService
          .loginWithTarl(personaUser.guid, personaUser.activePid)
          .pipe(
            catchError(() => of(null)),
            map(tarlUser =>
              currentPersonaUser(this.appConfig, personaUser, tarlUser),
            ),
            map(currentUser => new StoreCurrentUserAction({ currentUser })),
            tap(() => {
              this.router.navigateByUrl(targetUri.toString());
            }),
          ),
      ),
      catchError((error: unknown) => {
        const getErrorDetails = () => {
          if (error instanceof PersonaPadlockError) {
            return {
              title: $localize`:@@currentUserEffects-loginErrorTitle:Login error`,
              message: $localize`:@@currentUserEffects-loginErrorMessage:An error occurred during login`,
              code: '401',
            };
          }
          if (error instanceof AuthorizationFailedException) {
            return {
              title: $localize`:@@currentUserEffects-authorizationErrorTitle:Authorization error`,
              message: $localize`:@@currentUserEffects-authorizationErrorMessage:You do not have the required permissions to access this page`,
              code: '403',
            };
          }
          return {
            title: $localize`:@@currentUserEffects-unknownErrorTitle:Unknown error`,
            message: $localize`:@@currentUserEffects-unknownErrorMessage:An error occurred during login`,
            code: '500',
          };
        };

        const { title, message, code } = getErrorDetails();
        if (code === '500') {
          this.errorReportingService.reportToInstana(error);
        }
        const errorUri = new URI('/error.html');
        errorUri.addQuery('title', title);
        errorUri.addQuery('detail', message);
        errorUri.addQuery('code', code);

        this.router.navigateByUrl(errorUri.toString());

        return of(new StoreCurrentUserAction({ currentUser: null }));
      }),
    ),
  );

  logInWithOAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CurrentUserActionTypes.LOG_IN),
        tap(() => this.authService.login()),
      ),
    { dispatch: false },
  );

  logInWithOAuthDuringRouteNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CurrentUserActionTypes.LOG_IN_DURING_ROUTE_NAVIGATION),
        tap((action: LogInDuringRouteNavigationAction) =>
          this.authService.loginDuringRouteNavigation(action.payload.stateUrl),
        ),
      ),
    { dispatch: false },
  );

  logOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CurrentUserActionTypes.LOG_OUT),
        tap(() => this.authService.logout()),
      ),
    { dispatch: false },
  );

  updateAccessToken$ = createEffect(() =>
    this.authService.user$.pipe(
      map(user => user.accessToken),
      distinctUntilChanged(),
      map(accessToken => new StoreAccessTokenAction({ accessToken })),
    ),
  );
}
